/**
 * `normalizeHrInlineStyle` — `quoted_header_editor` 안의 `<hr>` 의 inline style 을 정리한다.
 *
 * 문제 배경:
 *   - 사용자가 `<hr style="border: none; height: 1px; color: #e1e1e1; background-color: #e1e1e1;" />`
 *     를 setData 로 입력하면 에디터 표시 시점에 다음과 같이 변형된다:
 *     `style="max-width: none; margin: 0px; width: 100%; border-width: medium;
 *      border-style: none; border-color: currentcolor; border-image: initial;
 *      height: 1px; color: rgb(225, 225, 225); background-color: rgb(225, 225, 225);"
 *      contenteditable="false"`.
 *   - 원인:
 *     1) `max-width / margin / width`: ngwEditor 라이브러리 `horizontal_rule.toDOM` 자동 prepend.
 *     2) `border-width / border-style / border-color / border-image`: 브라우저 CSSOM 의 `border: none`
 *        shorthand → longhand expand (border-color 의 initial 값이 `currentcolor`).
 *     3) `rgb(...)`: 브라우저 색상 정규화 (`#e1e1e1` → `rgb(225, 225, 225)`).
 *     4) `contenteditable="false"`: ProseMirror atom node 표준.
 *
 * 처리 정책 (setData / region.set 진입 직전 적용):
 *   - 적용 범위는 `quoted_header_editor` 안의 `<hr>` 만 (인용 헤더의 separator). 본문에 사용자가
 *     직접 삽입한 `<hr>` (라이브러리 `ngw-hr` variant) 은 영향 받지 않는다.
 *   - `contenteditable` attribute 제거 (송신 HTML 에 불필요한 PM 내부 표식).
 *   - inline style 정리:
 *     · border-* longhand 4개 (`border-width: medium`, `border-style: none`, `border-color: <any>`,
 *       `border-image: initial`) 가 모두 있으면 단일 `border: none` 으로 통합 → `currentcolor` 자동 제거.
 *     · 라이브러리가 prepend 한 표식 값 (`max-width: none`, `margin: 0` 또는 `margin: 0px`, `width: 100%`)
 *       을 단독으로 가지면 제거. 사용자 의도 값 (예: `margin: 10px`) 은 보존.
 *     · `color` / `background-color` 의 `rgb(r, g, b)` 정수 색상 → 6-digit hex 로 정규화.
 *     · 그 외 속성은 보존.
 *   - 멱등성: 두 번 호출해도 결과 동일. 라운드트립 안정성 확보.
 *
 * 한계: PM 의 DOM 부착 또는 라이브러리 NodeView 가 element.style 로 다시 set 하여 cssText expand 를
 * 트리거하면 본 정리가 무효화될 수 있다. 수동 검증에서 expand 재발생 시 horizontal_rule NodeView
 * override 를 NgwEditorLifecycle 의 `nodeViews` 에 등록하여 DOM 부착 책임을 가져오는 후속 작업이
 * 필요하다.
 */

const BORDER_LONGHAND_NAMES = [
  'border-width',
  'border-style',
  'border-color',
  'border-image',
] as const

type StyleEntry = { name: string; value: string }

export function normalizeHrInlineStyle(html: string): string {
  if (html.length === 0) return html
  if (!/<hr\b/i.test(html)) return html
  if (typeof DOMParser === 'undefined') return html

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
  const root = doc.body.firstElementChild
  if (root == null) return html

  const modified = normalizeHrInlineStyleDOM(root)
  return modified ? root.innerHTML : html
}

/**
 * normalizeHrInlineStyle 의 DOM variant — 문자열 재파싱 없이 이미 파싱된 트리 안의
 * quoted_header_editor hr 을 직접 정규화한다. reply 인용 DOM 직결 경로(fragmentToContainer)가
 * 사용한다. 문자열 경로와 동일 로직(normalizeOneHr)을 공유해 결과 parity 를 보장한다.
 * @returns 하나라도 수정됐으면 true (문자열 경로의 "미변경 시 원본 반환" 최적화에 사용).
 */
export function normalizeHrInlineStyleDOM(root: ParentNode): boolean {
  let modified = false
  const quotedHeaders = root.querySelectorAll('div.quoted_header_editor, div[id^="quoted_header_"]')
  for (const qh of Array.from(quotedHeaders)) {
    for (const hr of Array.from(qh.querySelectorAll('hr'))) {
      if (normalizeOneHr(hr)) modified = true
    }
  }
  return modified
}

function normalizeOneHr(hr: HTMLHRElement): boolean {
  let modified = false

  if (hr.hasAttribute('contenteditable')) {
    hr.removeAttribute('contenteditable')
    modified = true
  }

  const styleAttr = hr.getAttribute('style')
  if (styleAttr == null) return modified

  const entries = parseStyleString(styleAttr)
  if (entries.length === 0) {
    hr.removeAttribute('style')
    return true
  }

  const compacted = compactBorderLonghand(entries)
  const filtered = removeLibraryPrependMarkers(compacted)
  const normalized = filtered.map(normalizeColorValue)

  const newStyle = serializeStyleEntries(normalized)
  if (newStyle === styleAttr) return modified

  if (newStyle.length === 0) {
    hr.removeAttribute('style')
  } else {
    hr.setAttribute('style', newStyle)
  }
  return true
}

function parseStyleString(text: string): StyleEntry[] {
  const out: StyleEntry[] = []
  for (const raw of text.split(';')) {
    const trimmed = raw.trim()
    if (trimmed.length === 0) continue
    const colon = trimmed.indexOf(':')
    if (colon < 0) continue
    const name = trimmed.slice(0, colon).trim().toLowerCase()
    const value = trimmed.slice(colon + 1).trim()
    if (name.length === 0 || value.length === 0) continue
    out.push({ name, value })
  }
  return out
}

function serializeStyleEntries(entries: ReadonlyArray<StyleEntry>): string {
  return entries.map((e) => `${e.name}: ${e.value}`).join('; ')
}

/**
 * border-* longhand 4개가 모두 `border: none` 의미일 때 단일 `border: none` 으로 통합.
 * border-color 의 값은 임의 (currentcolor / 색상 / initial) — border-style: none 이면 색상 무관.
 * 한 항목이라도 없거나 border-style 이 'none' 이 아니면 원본 유지.
 */
function compactBorderLonghand(entries: ReadonlyArray<StyleEntry>): StyleEntry[] {
  const map = new Map<string, StyleEntry>()
  for (const e of entries) map.set(e.name, e)

  const allPresent = BORDER_LONGHAND_NAMES.every((n) => map.has(n))
  if (!allPresent) return entries.slice()

  const borderStyle = map.get('border-style')!.value.toLowerCase()
  if (borderStyle !== 'none') return entries.slice()
  const borderWidth = map.get('border-width')!.value.toLowerCase()
  if (borderWidth !== 'medium' && borderWidth !== '0' && borderWidth !== '0px') {
    return entries.slice()
  }

  // 4개 longhand 의 첫 위치에 `border: none` 삽입, 나머지 longhand 는 제거.
  const result: StyleEntry[] = []
  let inserted = false
  for (const e of entries) {
    if ((BORDER_LONGHAND_NAMES as ReadonlyArray<string>).includes(e.name)) {
      if (!inserted) {
        result.push({ name: 'border', value: 'none' })
        inserted = true
      }
      continue
    }
    result.push(e)
  }
  return result
}

/**
 * ngwEditor 라이브러리가 `horizontal_rule.toDOM` 에서 prepend 한 표식 값을 제거.
 * 표식 (`max-width: none`, `margin: 0|0px`, `width: 100%`) 은 라이브러리 default `.ngw-editor hr`
 * 의 시각을 inline 으로 덮어쓰기 위한 것 — 수신측에는 무의미하고, PM doc 의 rawStyle 에 들어가면
 * 라운드트립 시 매번 누적된다. 사용자 정의 값 (예: `margin: 10px`) 은 보존.
 */
function removeLibraryPrependMarkers(entries: ReadonlyArray<StyleEntry>): StyleEntry[] {
  return entries.filter((e) => {
    if (e.name === 'max-width' && e.value.toLowerCase() === 'none') return false
    if (e.name === 'margin' && (e.value === '0' || e.value.toLowerCase() === '0px')) {
      return false
    }
    if (e.name === 'width' && e.value === '100%') return false
    return true
  })
}

/**
 * `rgb(r, g, b)` 정수 색상을 6-digit hex 로 정규화. 일부 legacy 메일 클라이언트 호환성 + 의미 동등.
 * alpha 채널이 있는 `rgba(...)` 는 변환하지 않고 그대로 둔다.
 */
function normalizeColorValue(entry: StyleEntry): StyleEntry {
  if (entry.name !== 'color' && entry.name !== 'background-color') return entry
  const match = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i.exec(entry.value)
  if (match == null) return entry
  const [, r, g, b] = match
  const toHex = (n: string): string => {
    const v = Number(n)
    if (!Number.isFinite(v) || v < 0 || v > 255) return ''
    return v.toString(16).padStart(2, '0')
  }
  const hex = `${toHex(r!)}${toHex(g!)}${toHex(b!)}`
  if (hex.length !== 6) return entry
  return { name: entry.name, value: `#${hex}` }
}
